import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchableSingleValueField from "./SearchableSingleValueField";
import useNominatimSearch from "../utils/useNominatimSearch";
import { normalizeLocationFilterValues, normalizeLocationValueForSave } from "../utils/locationFilters";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
let platformLocationOptions = null;
let platformLocationOptionsRequest = null;

const fetchPlatformLocationOptions = async () => {
  if (platformLocationOptions) return platformLocationOptions;
  if (!platformLocationOptionsRequest) {
    platformLocationOptionsRequest = fetch(`${API_BASE}/api/companies/field-options`, {
      credentials: "include",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("Could not load location options");
        const payload = await response.json();
        platformLocationOptions = normalizeLocationFilterValues(payload?.locations || []);
        return platformLocationOptions;
      })
      .catch(() => [])
      .finally(() => {
        platformLocationOptionsRequest = null;
      });
  }
  return platformLocationOptionsRequest;
};

/**
 * Searchable single-value location input used by create/edit forms.
 * It combines known values with debounced geocoding suggestions while still
 * allowing a user to enter a location that is not returned by the service.
 */
export default function LocationSearchableField({
  value = "",
  onChange,
  onInputValueChange,
  options = [],
  loading = false,
  ...rest
}) {
  const [query, setQuery] = useState(value);
  const [platformOptions, setPlatformOptions] = useState(platformLocationOptions || []);
  const { results, loading: searching } = useNominatimSearch(query);

  useEffect(() => {
    let active = true;
    fetchPlatformLocationOptions().then((nextOptions) => {
      if (active) setPlatformOptions(nextOptions);
    });
    return () => {
      active = false;
    };
  }, []);

  const mergedOptions = useMemo(
    () => normalizeLocationFilterValues([value, ...options, ...platformOptions, ...results]),
    [options, platformOptions, results, value]
  );

  const handleInputValueChange = useCallback(
    (nextValue) => {
      setQuery(nextValue);
      onInputValueChange?.(nextValue);
    },
    [onInputValueChange]
  );

  return (
    <SearchableSingleValueField
      {...rest}
      value={value}
      options={mergedOptions}
      loading={loading || searching}
      normalizeInput={normalizeLocationValueForSave}
      onChange={onChange}
      onInputValueChange={handleInputValueChange}
      textFieldProps={{ autoComplete: "off", ...(rest.textFieldProps || {}) }}
    />
  );
}
